import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
   LcsDateRangePickerInputPanelContentFormValue,
   LcsDateRangePickerOption,
} from '@lcs/shared/datepicker';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
@Component({
   selector: 'ndt-date-filter',
   styleUrls: ['./date-filter.component.scss'],
   templateUrl: './date-filter.component.html',
   standalone: false,
})
export class DateFilterComponent {
   @Output() logQuickFilterChanged = new EventEmitter<{
      startDate: Date | undefined;
      endDate: Date | undefined;
   }>();

   @Input() fromDate: Date | null = null;

   @Input() toDate: Date | null = null;

   @Input() dateRangePickerOptions: LcsDateRangePickerOption<Date>[] | undefined;

   @Input() initialContentFormValue: LcsDateRangePickerInputPanelContentFormValue<Date> | undefined;

   dateFilterForm: UntypedFormGroup;

   minDate = new Date(2000, 0, 1);

   maxDate = new Date();

   maxDateString: string;

   startTime: Date = new Date();

   constructor(private formBuilder: UntypedFormBuilder) {
      this.maxDate = new Date();

      this.maxDateString = this.maxDate.toISOString().slice(0, 16);

      this.dateFilterForm = this.formBuilder.group(
         {
            fromDate: this.fromDate ? [this.fromDate] : [null],
            timePicker: [''],
            toDate: this.toDate ? [this.toDate] : [null],
         },
         { validators: this.dateRangeValidator }
      );

      this.dateFilterForm.valueChanges
         .pipe(
            debounceTime(10),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
         )
         .subscribe(() => {
            this.filterChange();
         });
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes.fromDate && changes.fromDate.currentValue !== undefined) {
         this.dateFilterForm.get('fromDate')?.setValue(this.fromDate);
      }
      if (changes.toDate && changes.toDate.currentValue !== undefined) {
         this.dateFilterForm.get('toDate')?.setValue(this.toDate);
      }
   }

   dateRangeValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
      const startDate = group.get('fromDate')?.value;
      const endDate = group.get('toDate')?.value;
      if (startDate && endDate && startDate > endDate) {
         group.get('toDate')?.setErrors({ invalidDateRange: true });
         group.get('fromDate')?.setErrors({ invalidDateRange: true });
         return { invalidDateRange: true };
      } else {
         group.get('toDate')?.setErrors(null);
         group.get('fromDate')?.setErrors(null);
         return null;
      }
   };

   filterChange() {
      if (this.dateFilterForm.errors && this.dateFilterForm.errors.invalidDateRange) {
         this.dateFilterForm.get('toDate')?.markAsTouched();
         this.dateFilterForm.get('fromDate')?.markAsTouched();
         return;
      }

      const fromDateControl = this.dateFilterForm.get('fromDate');
      const toDateControl = this.dateFilterForm.get('toDate');

      const startDate = fromDateControl?.value ? new Date(fromDateControl.value) : undefined;
      const endDate = toDateControl?.value ? new Date(toDateControl.value) : undefined;

      if (startDate) startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(23, 59, 59, 999);

      if (this.dateFilterForm.valid) {
         this.logQuickFilterChanged.emit({ startDate, endDate });
      }
   }
}
